import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { Select, TextInput, Textarea } from "flowbite-react";
import {
  HiOutlinePlus,
  HiPencilAlt,
  HiTrash,
  HiArchive,
  HiOutlineRefresh,
} from "react-icons/hi";
import { Button } from "../Button/Button";
import { StandardTable } from "../Table/StandardTable";
import { TableSkeleton } from "../Skeleton/TableSkeleton";
import { EntityFormDialog } from "../Dialog/EntityFormDialog";
import { FeatureDefinition } from "../../api/vehicleFeatures";
import { useFeatureSchema } from "./useFeatureSchema";

export function FeatureSettings({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation("workspace");
  const {
    schema,
    error,
    busy: locked,
    save: persist,
    reload,
    clearError,
  } = useFeatureSchema(workspaceId);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FeatureDefinition>();
  const [deleting, setDeleting] = useState<FeatureDefinition>();
  const [filter, setFilter] = useState("active");
  const [name, setName] = useState("");
  const [type, setType] = useState<FeatureDefinition["type"]>("boolean");
  const [unit, setUnit] = useState("");
  const [options, setOptions] = useState("");
  const visible =
    schema?.definitions.filter(
      (d) => filter === "all" || d.archived === (filter === "archived"),
    ) || [];
  const numericUnit =
    type === "number" &&
    !!unit.trim() &&
    unit !== editing?.unit &&
    Number.isFinite(Number(unit.trim().replace(",", ".")));
  const optionLabels = options
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const duplicate = schema?.definitions.some(
    (d) =>
      d.id !== editing?.id &&
      d.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase(),
  );
  const invalidOptions =
    type === "select" &&
    (!optionLabels.length ||
      optionLabels.length > 50 ||
      optionLabels.some((s) => s.length > 80) ||
      new Set(optionLabels.map((s) => s.toLocaleLowerCase())).size !==
        optionLabels.length);
  const add = async () => {
    if (
      !schema ||
      locked ||
      !name.trim() ||
      numericUnit ||
      duplicate ||
      invalidOptions
    )
      return;
    try {
      const definition: FeatureDefinition = {
        id: editing?.id || `f_${uuid().replace(/-/g, "")}`,
        name: name.trim(),
        type,
        unit: type === "number" ? unit.trim() : "",
        options:
          type === "select"
            ? optionLabels.map((label, index) => ({
                id:
                  editing?.options[index]?.id ||
                  `o_${uuid().replace(/-/g, "")}`,
                label,
              }))
            : [],
        archived: editing?.archived || false,
      };
      await persist({
        ...schema,
        definitions: editing
          ? schema.definitions.map((d) =>
              d.id === editing.id ? definition : d,
            )
          : [...schema.definitions, definition],
      });
      setCreating(false);
      setEditing(undefined);
    } catch {
      /* Keep the editor and its values open; error is shown inline. */
    }
  };
  const openEditor = (definition?: FeatureDefinition) => {
    clearError();
    setEditing(definition);
    setName(definition?.name || "");
    setType(definition?.type || "boolean");
    setUnit(definition?.unit || "");
    setOptions(definition?.options.map((o) => o.label).join("\n") || "");
    setCreating(true);
  };
  const closeEditor = () => {
    if (!locked) {
      setCreating(false);
      clearError();
    }
  };
  const closeDelete = () => {
    if (!locked) {
      setDeleting(undefined);
      clearError();
    }
  };
  const errorMessage = error && (
    <p role="alert" className="text-sm text-danger">
      {t(error)}{" "}
      <button
        type="button"
        className="underline"
        disabled={locked}
        onClick={() => {
          setCreating(false);
          setDeleting(undefined);
          reload();
        }}
      >
        {t("features.reload")}
      </button>
    </p>
  );
  const action = (
    id: string,
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
  ) => ({
    id,
    children: (
      <Button
        key={id}
        type="button"
        color="none"
        title={label}
        aria-label={label}
        disabled={locked}
        className="mx-0.5 flex h-8 w-8 items-center justify-center border-0 bg-transparent p-0 text-secondary shadow-none hover:text-brand sm:mx-1 sm:h-10 sm:w-10"
        onClick={onClick}
      >
        <span className="table-action-icon">{icon}</span>
      </Button>
    ),
  });
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-xl font-semibold text-text sm:text-2xl">
            {t("features.title")}
          </h2>
          <p className="pt-2 pb-4 text-base text-secondary">
            {t("features.settingsHelp")}
          </p>
        </div>
        <div className="flex w-full flex-col items-start gap-4 sm:w-auto sm:flex-row sm:gap-6">
          <Select
            aria-label={t("features.filter")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-auto"
          >
            {["active", "archived", "all"].map((value) => (
              <option key={value} value={value}>
                {t(`features.${value}`)}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="secondary"
            className="w-full whitespace-nowrap sm:w-auto"
            disabled={!schema || locked || schema.definitions.length >= 50}
            onClick={() => openEditor()}
          >
            <HiOutlinePlus className="mr-2 h-5 w-5" />
            {t("features.add")}
          </Button>
        </div>
      </div>
      {!creating && !deleting && errorMessage}
      {!schema && !error && <TableSkeleton />}
      {schema && (
        <StandardTable
          compact
          hasActions
          header={[
            {
              id: "name",
              children: t("features.name"),
              showOnSmallScreen: true,
            },
            {
              id: "type",
              children: t("features.type"),
              showOnSmallScreen: true,
            },
            { id: "unit", children: t("features.unitLabel") },
            { id: "status", children: t("features.status") },
          ]}
          rows={visible.map((d) => ({
            id: d.id,
            cells: [
              {
                id: "name",
                children: (
                  <span>
                    {d.name}
                    <span className="block text-xs text-secondary sm:hidden">
                      {d.unit}
                      {d.archived ? ` · ${t("features.archived")}` : ""}
                    </span>
                  </span>
                ),
                showOnSmallScreen: true,
                classOverride:
                  "max-w-xs whitespace-normal break-words px-3 py-2 text-sm text-text sm:text-base",
              },
              {
                id: "type",
                children: t(`features.${d.type}`),
                showOnSmallScreen: true,
              },
              { id: "unit", children: d.unit || "—" },
              {
                id: "status",
                children: t(
                  d.archived ? "features.archived" : "features.active",
                ),
              },
            ],
            actions: [
              action("edit", t("features.edit"), <HiPencilAlt />, () =>
                openEditor(d),
              ),
              action(
                "archive",
                t(d.archived ? "features.restore" : "features.archive"),
                d.archived ? <HiOutlineRefresh /> : <HiArchive />,
                () => {
                  void persist({
                    ...schema,
                    definitions: schema.definitions.map((item) =>
                      item.id === d.id
                        ? { ...item, archived: !item.archived }
                        : item,
                    ),
                  }).catch(() => {});
                },
              ),
              action("delete", t("features.delete"), <HiTrash />, () => {
                clearError();
                setDeleting(d);
              }),
            ],
          }))}
        />
      )}
      {schema && !visible.length && (
        <p role="status" className="text-sm text-secondary">
          {t(
            schema.definitions.length
              ? "features.emptyFilter"
              : "features.empty",
          )}
        </p>
      )}
      {creating && (
        <EntityFormDialog
          title={t(editing ? "features.edit" : "features.add")}
          show
          onCancel={closeEditor}
        >
          <div className="space-y-4 text-text">
            <p className="text-sm text-secondary">
              {t("features.definitionHelp")}
            </p>
            <label className="block text-sm font-medium">
              {t("features.name")}
              <TextInput
                autoFocus
                className="mt-2"
                value={name}
                maxLength={80}
                disabled={locked}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="block text-sm font-medium">
              {t("features.type")}
              <Select
                className="mt-2"
                value={type}
                disabled={locked}
                onChange={(e) =>
                  setType(e.target.value as FeatureDefinition["type"])
                }
              >
                {["boolean", "number", "select", "string"].map((v) => (
                  <option key={v} value={v}>
                    {t(`features.${v}`)}
                  </option>
                ))}
              </Select>
            </label>
            {type === "number" && (
              <label className="block text-sm font-medium">
                {t("features.unit")}
                <TextInput
                  className="mt-2"
                  value={unit}
                  maxLength={20}
                  placeholder={t("features.unitPlaceholder")}
                  disabled={locked}
                  onChange={(e) => setUnit(e.target.value)}
                />
                <span className="mt-2 block text-sm font-normal text-secondary">
                  {t("features.unitHelp")}
                </span>
              </label>
            )}
            {type === "select" && (
              <label className="block text-sm font-medium">
                {t("features.options")}
                <Textarea
                  className="mt-2"
                  rows={4}
                  value={options}
                  disabled={locked}
                  onChange={(e) => setOptions(e.target.value)}
                />
              </label>
            )}
            {(numericUnit || duplicate || invalidOptions) && (
              <p role="alert" className="text-sm text-danger">
                {t(
                  numericUnit
                    ? "features.unitNotValue"
                    : duplicate
                      ? "features.duplicateName"
                      : "features.invalidOptions",
                )}
              </p>
            )}
            <p className="text-sm text-secondary">{t("features.editHelp")}</p>
            {errorMessage}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                disabled={locked}
                onClick={closeEditor}
              >
                {t("features.cancel")}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={
                  locked ||
                  !name.trim() ||
                  numericUnit ||
                  duplicate ||
                  invalidOptions
                }
                onClick={() => void add()}
              >
                {t(locked ? "features.loading" : "features.saveDefinition")}
              </Button>
            </div>
          </div>
        </EntityFormDialog>
      )}
      {deleting && (
        <EntityFormDialog
          title={t("features.deleteTitle")}
          show
          onCancel={closeDelete}
        >
          <div className="space-y-4 text-text">
            <p>{t("features.deleteHelp", { name: deleting.name })}</p>
            {errorMessage}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                disabled={locked}
                onClick={closeDelete}
              >
                {t("features.cancel")}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={locked}
                onClick={() => {
                  if (!schema) return;
                  void persist({
                    ...schema,
                    definitions: schema.definitions.filter(
                      (d) => d.id !== deleting.id,
                    ),
                  })
                    .then(() => setDeleting(undefined))
                    .catch(() => {});
                }}
              >
                {t("features.delete")}
              </Button>
            </div>
          </div>
        </EntityFormDialog>
      )}
    </div>
  );
}
